import apiClient from "@/api/client";
import { triggerBrowserDownload } from "@/utils/download";

/**
 * Opening a file from the ManagedFile registry.
 *
 * Every serializer that embeds a file (`ManagedFileBriefSerializer` and friends)
 * carries `url` — a presigned S3 link SIGNED WHEN THE PAYLOAD WAS BUILT and dead
 * an hour later (AWS_QUERYSTRING_EXPIRE = 3600, minus up to 5 min of the presign
 * cache in apps/core/storage.py). The app never refetches on window focus
 * (queryClient: refetchOnWindowFocus false), so a page left open past the hour —
 * a WPW mass sequence, a requisition waiting for approval — hands the user a link
 * that answers "AccessDenied / Request has expired" from S3.
 *
 * So the click asks the API for a link signed NOW. The link from the payload stays
 * as the fallback for when that call itself fails (offline, 401 in flight): within
 * the hour it still works, and outside it nothing worse happens than before.
 */
export interface DownloadableFile {
  id: string;
  original_filename: string;
  /** Presigned link from the payload — only a fallback, see above. */
  url?: string | null;
}

export const FILE_UNAVAILABLE_MSG =
  "Plik nie jest dostępny do pobrania (mógł zostać usunięty albo czeka na skan antywirusowy).";
export const FILE_FAILED_MSG = "Nie udało się pobrać pliku. Spróbuj ponownie.";

/** Freshly signed link, or null when the file has no downloadable content. */
export const freshManagedFileUrl = (managedFileId: string): Promise<string | null> =>
  apiClient
    .get<{ data: { url: string | null; original_filename: string } }>(`/files/${managedFileId}/`)
    .then((r) => r.data.data.url);

/** Download `file` through a link that is valid at the moment of the click. */
export async function openManagedFile(file: DownloadableFile): Promise<void> {
  let url: string | null;
  try {
    url = await freshManagedFileUrl(file.id);
  } catch {
    url = file.url ?? null;
    if (!url) {
      window.alert(FILE_FAILED_MSG);
      return;
    }
  }
  if (!url) {
    window.alert(FILE_UNAVAILABLE_MSG);
    return;
  }
  triggerBrowserDownload(url, file.original_filename);
}

/**
 * Should this click be taken over (re-signed) instead of following the href?
 *
 * A modified click — ctrl/cmd/shift/alt, or anything but the primary button — is the
 * user asking the browser for a new tab / "save link as". Let those keep the native
 * behaviour on the link from the payload; within its hour it works exactly as before.
 */
export function isPlainLeftClick(e: { button?: number; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean }): boolean {
  return (e.button ?? 0) === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
}

/**
 * Uploading to the ManagedFile registry (POST /files/).
 *
 * The API refuses a file over 500 MB (ManagedFileUploadSerializer), but only once the
 * whole body has arrived — on production a >500 MB file took ~85 s per attempt and the
 * screens dropped the reason ("Plik zbyt duży"), so the same person retried five times
 * (2026-10-20). Every caller goes through `uploadManagedFile`, which refuses it before
 * a single byte is sent, and shows failures with `uploadErrorMessage`.
 */
export const MANAGED_FILE_MAX_BYTES = 500 * 1024 * 1024;

export function fileTooLargeMessage(file: File): string {
  const mb = Math.ceil(file.size / (1024 * 1024));
  return `Plik „${file.name}” ma ${mb} MB — maksymalny rozmiar pliku to 500 MB.`;
}

export class FileTooLargeError extends Error {
  readonly file: File;

  constructor(file: File) {
    super(fileTooLargeMessage(file));
    this.name = "FileTooLargeError";
    this.file = file;
  }
}

/** First of `files` over the registry limit, if any — for checks before a save. */
export function findTooLargeFile(files: Iterable<File>): File | undefined {
  for (const file of files) if (file.size > MANAGED_FILE_MAX_BYTES) return file;
  return undefined;
}

export async function uploadManagedFile<T = { id: string; url: string | null }>(file: File): Promise<T> {
  if (file.size > MANAGED_FILE_MAX_BYTES) throw new FileTooLargeError(file);
  const form = new FormData();
  form.append("file", file);
  // The client sends JSON by default — multipart has to be set explicitly.
  const r = await apiClient.post<{ data: T }>("/files/", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data.data;
}

/**
 * What to tell the user about a failed upload: the size refusal above, the reason the
 * API gave (`errors` is a string, `{ detail }` or DRF's `{ field: [...] }`), or
 * `fallback` when there is none (network error, 5xx).
 */
export function uploadErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof FileTooLargeError) return err.message;
  const errors = (err as { response?: { data?: { errors?: unknown } } } | null)?.response?.data?.errors;
  if (typeof errors === "string" && errors) return errors;
  if (errors && typeof errors === "object") {
    const messages = Object.values(errors as Record<string, unknown>)
      .flat()
      .filter((v): v is string => typeof v === "string" && v.length > 0);
    if (messages.length) return messages.join(" ");
  }
  return fallback;
}
